import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Button,
  Modal,
  Spin,
  Form,
  Input,
  Radio,
  DatePicker,
  Upload,
  Select,
  message,
} from 'antd';
import locale from 'antd/es/date-picker/locale/zh_CN';
import { addGlobalEventListener } from '@/utils/emit';
import { GlobalEventName } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import { accidentApi } from '@/fetch/bussiness/accident';
import { request } from '@/fetch/core';
import { buildURL } from '@/fetch/core/util';
import PreviewList from '@/components/PreviewList';
import { debounce } from '@/utils';
import {
  FIELD_NAMES,
  FORM_FIELD_CONFIG,
  trimValidator,
  ValidationMap,
  ValidationCtx,
} from './constants';
import UseGetCommonEnumMap from '@/utils/hooks/UseGetCommonEnumMap';
import { CommonEnumKey } from '@/utils/enum';
import { sendGlobalEvent } from '@/utils/emit';

interface AccidentOrderAddModalProps {
  // 车号
  vehicleName: string;
  // 工单编号，用于事故单和工单关联（只有从工单抽屉提报的事故单才需要此字段，即才需要建立关联关系，从单车页提报的不需要）
  issueNumber?: string;
  // 是否显示弹窗
  visible?: boolean;
  // 关闭弹窗的回调
  onCancel: () => void;
}

const AccidentOrderAddModal = ({
  vehicleName,
  issueNumber,
  visible = true,
  onCancel,
}: AccidentOrderAddModalProps) => {
  const [form] = Form.useForm(); // 使用 Form.useForm() 替代 formInstance ref
  const [fileList, setFileList] = useState<any[]>([]); // 文件列表状态
  const fileUploading = useRef<boolean>(false); // 文件是否正在上传中的loading态
  const [uploadLoading, setUploadLoading] = useState<boolean>(false); // 文件上传的视觉loading态
  const [loading, setLoading] = useState<boolean>(false); // 提交事故单的loading态
  // 地图搜索相关状态
  const [mapSearchOptions, setMapSearchOptions] = useState<Array<{
    label: string;
    value: string;
    address: string;
    longitude: number;
    latitude: number;
  }>>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    longitude?: number;
    latitude?: number;
  }>({});
  const fetchRef = useRef(0);  // 用于防止旧的搜索结果覆盖新结果
  const { enumMap, loadingEnumMap } = UseGetCommonEnumMap([
    CommonEnumKey.NEW_ACCIDENT_LEVEL_ENUM,
    CommonEnumKey.ACCIDENT_RESPONSIBLE_ENUM,
  ]); // 公共枚举值（"事故等级"和"有无责任"的下拉框数据）

  /**
   * 页面挂载--建立监听器，监听文件上传状态
   */
  useEffect(() => {
    const listener = (res: any) => {
      if (res.fieldName === 'technicalSupportAttmentList') {
        fileUploading.current = res.uploading;
      }
    };
    addGlobalEventListener(GlobalEventName.JDX_FILE_UPLOAD_STATUS, listener);
    return () => {
      removeEventListener(GlobalEventName.JDX_FILE_UPLOAD_STATUS, listener);
    };
  }, []);

  /**
   * 自定义上传请求
   * @param {*} option
   */
  const customRequest = async (option: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(option.file);
    reader.onloadend = async () => {
      option.onSuccess();
      const formData = new FormData();
      formData.append('file', option.file);

      const requestParam: RequestOptions = {
        method: 'POST',
        path: FORM_FIELD_CONFIG.technicalSupportAttmentList.actionUrl,
        contentType: 'formData',
        body: formData,
      };

      try {
        const res: any = await request(requestParam);
        if (res.code === HttpStatusCode.Success) {
          const newFile = {
            uid: option.file.uid,
            name: option.file.name,
            fileKey: res.data.fileKey,
            type: option.file.type?.startsWith('video')
              ? 'video'
              : option.file.type?.startsWith('image')
                ? 'image'
                : 'other',
            url: URL.createObjectURL(option.file),
          };
          setFileList((prev) => [...prev, newFile]);
        } else {
          message.error(res.message);
        }
      } catch (error: any) {
        message.error(error?.message || '上传失败');
      } finally {
        sendGlobalEvent(GlobalEventName.JDX_FILE_UPLOAD_STATUS, {
          fieldName: 'technicalSupportAttmentList',
          uploading: false,
        });
        setUploadLoading(false);
      }
    };
  };

  /**
   * 文件删除回调
   * @param {*} file
   */
  const handleFileDelete = (file: any) => {
    setFileList((prev) =>
      prev.filter((item) => item.uid !== file.uid)
    );
  };

  /**
   * Upload 组件配置
   */
  const uploadProps = {
    accept: 'image/*,video/*',
    maxCount: FORM_FIELD_CONFIG.technicalSupportAttmentList.max,
    listType: 'picture-card' as const,
    showUploadList: false, // 使用 PreviewList 展示
    customRequest,
    beforeUpload: () => {
      if (fileList.length >= FORM_FIELD_CONFIG.technicalSupportAttmentList.max) {
        message.error(
          `最多支持上传${FORM_FIELD_CONFIG.technicalSupportAttmentList.max}个文件！`
        );
        return Upload.LIST_IGNORE;
      }
      sendGlobalEvent(GlobalEventName.JDX_FILE_UPLOAD_STATUS, {
        fieldName: 'technicalSupportAttmentList',
        uploading: true,
      });
      setUploadLoading(true);
      return true;
    },
  };

  /**
   * 调用腾讯地图API搜索地理位置
   */
  const handleMapSearch = (searchText: string) => {
    if (!searchText || searchText.trim() === '') {
      setMapSearchOptions([]);
      return;
    }

    fetchRef.current += 1;
    const fetchId = fetchRef.current;
    setSearchLoading(true);

    const win = window as any;
    win.$.ajax({
      type: 'get',
      url: buildURL({
        absoluteURL: 'https://apis.map.qq.com/ws/place/v1/suggestion',
        urlParams: {
          key: 'V2LBZ-Y63CJ-YU7F3-X3WQW-7NFXH-RABW4',
          keyword: encodeURI(searchText),
          get_subpois: 1,
          output: 'jsonp',
        },
      }),
      dataType: 'jsonp',
      xhrFields: { mode: 'no-cors' },
      success: (res: any) => {
        if (fetchId !== fetchRef.current) {
          return; // 忽略旧的请求结果
        }

        if (res.status === 0) {
          const result = res?.data?.map((item: any, index: number) => ({
            label: item.title + '-' + item.address,
            value: item.location.lng + ',' + item.location.lat + ':' + index,
            address: item.title + '-' + item.address,
            longitude: item.location.lng,
            latitude: item.location.lat,
          })) || [];
          setMapSearchOptions(result);
        } else {
          message.error('搜索地址失败，请重试');
          setMapSearchOptions([]);
        }
        setSearchLoading(false);
      },
      error: () => {
        if (fetchId !== fetchRef.current) {
          return;
        }
        message.error('搜索地址失败，请重试');
        setMapSearchOptions([]);
        setSearchLoading(false);
      },
    });
  };

  // 创建防抖版本的搜索函数（300ms延迟）
  const debouncedMapSearch = useMemo(
    () => debounce(handleMapSearch, 300),
    []
  );

  /**
   * 提交前校验事故单字段
   * @return {*}
   */
  const handleValidateBeforeSubmit = () => {
    // 构造校验上下文
    const ctx: ValidationCtx = {
      fileUploading: fileUploading.current,
      vehicleName,
      formInstance: form, // 传递 form 实例
    };
    // 校验
    const error = ValidationMap.find((item) => item.when(ctx));
    if (!error) return true;
    // 校验不通过提示错误信息
    message.error(error.message || '请完善表单信息！');
    return false;
  };

  /**
   * 提交事故单
   * @return {*}
   */
  const handleSubmit = async () => {
    const isValidate = handleValidateBeforeSubmit();
    if (!isValidate) return;
    try {
      setLoading(true);
      const fieldsValue = await form.validateFields();
      const saveValue: any = {
        technicalSupportDescription: fieldsValue.technicalSupportDescription,
        technicalSupportAccidentLevel:
          fieldsValue.technicalSupportAccidentLevel,
        responsible: fieldsValue.responsible,
        vehicleName,
        accidentAddress: fieldsValue.accidentAddress,
        accidentTime: fieldsValue.accidentTime.format('YYYY-MM-DD HH:mm:ss'),
        technicalSupportAttmentList:
          fileList.length > 0 ? fileList : null,
        issueNumber: issueNumber || null,
        // 新增：如果用户通过地图搜索选择了地址，则附加经纬度信息
        ...(selectedLocation.longitude !== undefined && {
          accidentLongitude: selectedLocation.longitude,
        }),
        ...(selectedLocation.latitude !== undefined && {
          accidentLatitude: selectedLocation.latitude,
        }),
      };
      const res = await accidentApi.manualCreateAccident(saveValue);
      if (res && res.code === HttpStatusCode.Success) {
        message.success('创建成功！');
        onCancel && onCancel();
      } else {
        throw new Error(res?.message || '创建失败！');
      }
    } catch (error: any) {
      message.error(error?.message || '请完善表单信息！');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 渲染底部按钮
   */
  const renderModalFooter = () => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
      <Button onClick={onCancel}>取消</Button>
      <Button onClick={handleSubmit} loading={loading} type="primary">
        确定
      </Button>
    </div>
  );

  return (
    <Modal
      title="创建事故"
      onCancel={onCancel}
      width={800}
      open={visible}
      footer={renderModalFooter()}
    >
      <Spin spinning={loadingEnumMap}>
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 16 }}
          className="accident-form"
          colon={false}
        >
          {/* 有无责任 - Radio Group */}
          <Form.Item
            name={FIELD_NAMES.responsible}
            label={FORM_FIELD_CONFIG.responsible.label}
            rules={[{ required: true, message: '请选择有无责任' }]}
          >
            <Radio.Group
              options={enumMap[CommonEnumKey.ACCIDENT_RESPONSIBLE_ENUM]}
            />
          </Form.Item>

          {/* 事故等级 - Radio Group */}
          <Form.Item
            name={FIELD_NAMES.technicalSupportAccidentLevel}
            label={FORM_FIELD_CONFIG.technicalSupportAccidentLevel.label}
            rules={[{ required: true, message: '请选择事故等级' }]}
          >
            <Radio.Group
              options={enumMap[CommonEnumKey.NEW_ACCIDENT_LEVEL_ENUM]}
            />
          </Form.Item>

          {/* 发生位置 - Select with Map Search */}
          <Form.Item
            name={FIELD_NAMES.accidentAddress}
            label={FORM_FIELD_CONFIG.accidentAddress.label}
            rules={[
              { required: true, message: '请输入详细位置' },
              trimValidator('发生位置'),
            ]}
          >
            <Select
              showSearch
              allowClear
              placeholder="请搜索并选择位置"
              filterOption={false}
              onSearch={debouncedMapSearch}
              options={mapSearchOptions}
              notFoundContent={searchLoading ? <Spin size="small" /> : <span style={{ color: '#fff' }}>暂无数据</span>}
              onChange={(value, option: any) => {
                if (!value || !option) {
                  // 用户清空选择
                  setSelectedLocation({});
                  form.setFieldsValue({ accidentAddress: '' });
                  return;
                }

                // 用户选择了某个地址
                form.setFieldsValue({ accidentAddress: option.address });
                setSelectedLocation({
                  longitude: option.longitude,
                  latitude: option.latitude,
                });
              }}
            />
          </Form.Item>

          {/* 问题描述 - TextArea */}
          <Form.Item
            name={FIELD_NAMES.technicalSupportDescription}
            label={FORM_FIELD_CONFIG.technicalSupportDescription.label}
            rules={[
              { required: true, message: '请输入事故描述' },
              trimValidator('问题描述'),
            ]}
          >
            <Input.TextArea
              placeholder={
                FORM_FIELD_CONFIG.technicalSupportDescription.placeholder
              }
              maxLength={FORM_FIELD_CONFIG.technicalSupportDescription.maxLength}
              autoSize={{ minRows: 2, maxRows: 6 }}
              showCount
            />
          </Form.Item>

          {/* 事故发生时间 - DatePicker */}
          <Form.Item
            name={FIELD_NAMES.accidentTime}
            label={FORM_FIELD_CONFIG.accidentTime.label}
            rules={[{ required: true, message: '请选择时间' }]}
          >
            <DatePicker
              locale={locale}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              allowClear
            />
          </Form.Item>

          {/* 补充凭证 - Upload + PreviewList */}
          <Form.Item
            name={FIELD_NAMES.technicalSupportAttmentList}
            label={`${FORM_FIELD_CONFIG.technicalSupportAttmentList.label}(${fileList.length}/${FORM_FIELD_CONFIG.technicalSupportAttmentList.max})`}
          >
            <div className="upload-wrapper">
              {/* 使用 PreviewList 展示已上传文件 */}
              <PreviewList
                fill={true}
                fileList={fileList}
                onDelete={handleFileDelete}
                onPreview={() => {}}
              />
              {/* 上传中的 loading 指示器 */}
              {uploadLoading && (
                <div style={{ marginBottom: 8 }}>
                  <Spin size="small" /> <span style={{ marginLeft: 8 }}>正在上传中...</span>
                </div>
              )}
              {/* Upload 组件提供上传功能 */}
              <Upload {...uploadProps}>
                {fileList.length <
                  FORM_FIELD_CONFIG.technicalSupportAttmentList.max && (
                  <div>+</div>
                )}
              </Upload>
            </div>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AccidentOrderAddModal;
